import { gql, useMutation, useApolloClient } from '@apollo/client'
import { difference } from 'lodash'
import { ReactElement, useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'

import { Content, ContentHeader } from 'components/v2/Content'
import Heading from 'components/v2/Heading'
import Card from '@10xLabs/react-web/components/Card'
import { NewUser } from 'features/Users/components/NewUser'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { resolvers as loginResolvers } from 'resolvers/login'
import { canWrite, getPermissionIDsByRoles } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import { INewUserValues } from 'features/Users/types'

const messages = defineMessages({
  helmet: {
    id: 'users.new.helmet',
    defaultMessage: 'New User | Nexbus Admin'
  }
})

const CREATE_USER = gql`
  mutation CreateUser($user: createUserInput!) {
    createUser(input: $user)
  }
`

const NewUserView = (): ReactElement => {
  const navigate = useNavigate()
  const intl = useIntl()
  const client = useApolloClient()
  client.addResolvers(loginResolvers)

  const login = useLogin()

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  const [createUser, { loading, error }] = useMutation(CREATE_USER, {
    onCompleted() {
      navigate('/users')
    },
    onError: () => undefined
  })

  if (!canWrite(FEATURES.USER, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.USER} />
  }

  const handleSubmit = (user: INewUserValues): void => {
    const rolesPermissionIds = getPermissionIDsByRoles(user.roles)

    createUser({
      variables: {
        user: {
          firstName: user.firstName,
          lastName: user.lastName,
          username: user.username,
          email: user.email,
          password: user.password,
          position: user.position,
          companyIDs: user.companies.map(({ id }): string => id),
          policyIDs: [...user.roles.map(({ id }): string => id), ...difference(user.permissionIDs, rolesPermissionIds)],
          mfaEnabled: user.mfaEnabled,
          passwordExpirationEnabled: user.passwordExpirationEnabled
        }
      }
    })
  }

  return (
    <Content hasMaxWidth>
      <ContentHeader>
        <Heading.H3>
          <FormattedMessage id="users.new.title" defaultMessage="Create user" />
        </Heading.H3>
      </ContentHeader>
      <section>
        <Card.Container>
          <NewUser onSubmit={handleSubmit} loading={loading} error={error} />
        </Card.Container>
      </section>
    </Content>
  )
}

export default withLastUrl(NewUserView)
